(() => {
  if (window.foodyHomeInitialized) return;
  window.foodyHomeInitialized = true;
  const desktop = matchMedia('(min-width:1000px)');
  const reduceMotion = matchMedia('(prefers-reduced-motion:reduce)');
  const cleanups = new WeakMap();

  function initKit(root) {
    if (cleanups.has(root)) return;
    const form = root.querySelector('.foody-kit-form');
    if (!form) return;
    const controller = new AbortController();
    const gallery = root.querySelector('[data-kit-gallery]');
    let slides = gallery ? [...gallery.querySelectorAll('.swiper-slide')] : [];
    const initialImageId = form.querySelector('[data-kit-variant]:checked')?.dataset.imageId;
    const initialSlide = Math.max(0, slides.findIndex(slide => slide.dataset.kitImageId === initialImageId));
    const gallerySwiper = gallery && typeof window.Swiper === 'function' ? new window.Swiper(gallery, {
      slidesPerView: 1, initialSlide, rewind: true, speed: reduceMotion.matches ? 0 : 400,
      navigation: { prevEl: root.querySelector('.foody-kit-prev'), nextEl: root.querySelector('.foody-kit-next') },
      a11y: { enabled: true }, watchOverflow: true
    }) : null;
    let lastVariant, lastProduct;
    cleanups.set(root, () => { controller.abort(); gallerySwiper?.destroy(true, true); });
    const opts = { signal: controller.signal };
    const radios = [...form.querySelectorAll('[data-kit-variant]')];
    const quantity = form.elements.quantity;
    const submit = form.querySelector('[data-kit-submit]');
    const message = form.querySelector('[data-kit-message]');
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: root.dataset.currency || 'USD' });
    let busy = false;
    function update(reset = false) {
      const selected = radios.find(input => input.checked);
      if (!selected) { submit.disabled = true; return; }
      const min = Number(selected.dataset.min) || 1;
      const step = Number(selected.dataset.step) || 1;
      const max = Number(selected.dataset.max) || Infinity;
      let value = reset ? min : Number(quantity.value);
      value = Math.max(min, Math.min(max, value));
      value = min + Math.floor((value - min) / step) * step;
      quantity.value = String(value);
      window.FoodyText.set(form.querySelector('[data-kit-quantity]'), String(value));
      form.querySelector('[data-kit-minus]').disabled = busy || value <= min;
      form.querySelector('[data-kit-plus]').disabled = busy || value + step > max;
      submit.disabled = busy || selected.disabled;
      radios.forEach(input => { input.closest('[data-kit-option]').setAttribute('aria-pressed', String(input.checked)); });
      const price = Number(selected.dataset.price);
      window.FoodyText.set(root.querySelector('[data-kit-unit-price]'), formatter.format(price / 100));
      window.FoodyText.set(form.querySelector('[data-kit-total]'), formatter.format(price * value / 100));
      window.FoodyText.set(form.querySelector('[data-kit-selected]'), selected.dataset.title);
      if (lastVariant !== selected.value) {
        if (lastProduct !== selected.dataset.productId) {
          const template = root.querySelector('[data-kit-product-gallery="' + selected.dataset.productId + '"]');
          if (template && gallery) {
            gallery.querySelector('.swiper-wrapper').replaceChildren(template.content.cloneNode(true));
            slides = [...gallery.querySelectorAll('.swiper-slide')];
            root.querySelectorAll('.foody-kit-arrow').forEach(button => { button.hidden = slides.length <= 1; });
            gallerySwiper?.update();
          }
          lastProduct = selected.dataset.productId;
        }
        const index = slides.findIndex(slide => slide.dataset.kitImageId === selected.dataset.imageId);
        if (gallerySwiper) gallerySwiper.slideTo(Math.max(0, index));
        const image = root.querySelector('[data-kit-image]');
        if (image) { image.src = selected.dataset.image; image.alt = selected.dataset.title; }
        lastVariant = selected.value;
      }
    }
    form.addEventListener('change', event => { if (event.target.matches('[data-kit-variant]')) update(true); }, opts);
    form.addEventListener('click', event => {
      const button = event.target.closest('[data-kit-minus], [data-kit-plus]');
      if (!button || busy) return;
      const selected = radios.find(input => input.checked);
      const step = Number(selected?.dataset.step) || 1;
      quantity.value = String(Number(quantity.value) + (button.hasAttribute('data-kit-minus') ? -step : step));
      update();
    }, opts);
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || submit.disabled) return;
      const body = new FormData(form);
      busy = true;
      update();
      window.FoodyText.set(message, '');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body, signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || root.dataset.error);
        window.FoodyText.set(message, root.dataset.added);
        document.dispatchEvent(new CustomEvent('foody:cart-added'));
      } catch (error) {
        if (error.name !== 'AbortError') window.FoodyText.set(message, error.message || root.dataset.error);
      } finally { busy = false; if (!controller.signal.aborted) update(); }
    }, opts);
    update();
  }

  function initStories(root) {
    if (cleanups.has(root)) return;
    const slider = root.querySelector('.stories-swiper');
    const video = root.querySelector('video');
    const dialog = root.querySelector('[data-story-dialog]');
    const status = root.querySelector('#video-status');
    const controller = new AbortController();
    const opts = { signal: controller.signal };
    const stop = () => { video.pause(); video.removeAttribute('src'); video.load(); };
    if (typeof window.Swiper === 'function' && slider.querySelector('.swiper-slide')) {
      const config = JSON.parse(root.querySelector('[data-story-config]').textContent);
      const max = Math.max(config.slidesPerView, ...Object.values(config.breakpoints).map(b => b.slidesPerView));
      config.loop = config.loop && slider.querySelectorAll('.swiper-slide').length >= max + 1;
      new window.Swiper(slider, { ...config, slidesPerGroup: 1, speed: reduceMotion.matches ? 0 : 500, pagination: { el: root.querySelector('.stories-pagination'), clickable: true }, navigation: { prevEl: root.querySelector('.stories-prev'), nextEl: root.querySelector('.stories-next') }, a11y: { enabled: true }, watchOverflow: true });
    }
    root.addEventListener('click', event => {
      const card = event.target.closest('[data-story-src]');
      if (card) {
        video.pause();
        video.poster = card.dataset.storyPoster;
        video.src = card.dataset.storySrc;
        video.setAttribute('aria-label', card.dataset.storyTitle);
        window.FoodyText.set(root.querySelector('[data-story-heading]'), card.dataset.storyTitle, card);
        window.FoodyText.set(root.querySelector('#story-caption'), card.dataset.storyCaption, card);
        root.querySelector('[data-demo-note]').hidden = card.dataset.storyDemo !== 'true';
        window.FoodyText.set(status, '');
        dialog.showModal();
        video.play().catch(() => { if (dialog.open) window.FoodyText.set(status, status.dataset.playMessage); });
      } else if (event.target.closest('[data-story-close]')) dialog.close();
      else if (event.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      }
    }, opts);
    dialog.addEventListener('close', stop, opts);
    video.addEventListener('error', () => { if (video.getAttribute('src')) window.FoodyText.set(status, status.dataset.errorMessage); }, opts);
    video.addEventListener('playing', () => { window.FoodyText.set(status, ''); }, opts);
    document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); }, opts);
    cleanups.set(root, () => { dialog.close(); stop(); slider.swiper?.destroy(true, true); controller.abort(); });
  }

  function syncFooters() {
    document.querySelectorAll('[data-foody-footer] details').forEach(detail => { detail.open = desktop.matches; });
  }
  function initialize(scope = document) {
    scope.querySelectorAll('[data-foody-starter]').forEach(initKit);
    scope.querySelectorAll('[data-foody-stories]').forEach(initStories);
    syncFooters();
  }
  window.foodySelectScent = (name) => {
    const kit = document.querySelector('[data-foody-starter]');
    if (!kit) return false;
    const normalized = name.trim().toLowerCase();
    const candidates = [...kit.querySelectorAll('[data-kit-variant]')].filter(input => !input.disabled && [input.dataset.title, input.dataset.optionOne].some(value => value?.trim().toLowerCase() === normalized));
    if (candidates.length !== 1) return false;
    const selected = candidates[0];
    selected.checked = true;
    selected.dispatchEvent(new Event('change', { bubbles: true }));
    document.querySelectorAll('[data-quiz-dialog][open]').forEach(dialog => dialog.close());
    kit.scrollIntoView({ behavior: reduceMotion.matches ? 'instant' : 'smooth', block: 'start' });
    selected.focus({ preventScroll: true });
    return true;
  };
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const scent = event.target.closest('[data-foody-select-scent]');
    if (scent && window.foodySelectScent(scent.dataset.foodySelectScent)) { event.preventDefault(); return; }
    const link = event.target.closest('[data-foody-url]');
    if (link && !link.disabled && link.dataset.foodyUrl) window.location.assign(link.dataset.foodyUrl);
  });
  desktop.addEventListener('change', syncFooters);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-foody-starter], [data-foody-stories]').forEach(root => { cleanups.get(root)?.(); cleanups.delete(root); });
  });
})();
